// User-safe API error normalisation.
// Kept dependency-free so it can be unit-tested without pulling axios.

const GENERIC_ERR = "Something went wrong. Please try again.";

// Pretty-print a Pydantic-style validation error item, e.g.
//   { type: "missing", loc: ["body", "reply_to"], msg: "Field required" }
// becomes "Reply to: Field required".
const _formatValidationItem = (item) => {
  if (!item || typeof item !== "object") return null;
  const msg = typeof item.msg === "string" ? item.msg : null;
  const locArr = Array.isArray(item.loc) ? item.loc : [];
  const rawField = [...locArr]
    .reverse()
    .find(
      (seg) =>
        typeof seg === "string" && seg !== "body" && seg !== "query" && seg !== "path"
    );
  if (!msg) return null;
  if (!rawField) return msg;
  const label = String(rawField)
    .replace(/[_\-.]+/g, " ")
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
  return `${label}: ${msg}`;
};

/**
 * Normalise any thrown value (axios error, plain Error, string, unknown object)
 * into a user-safe string suitable for toasts and inline error banners.
 *
 * Never returns an object — safe to render directly in JSX.
 */
export const formatErr = (e) => {
  if (e == null) return GENERIC_ERR;
  if (typeof e === "string") return e;

  const detail = e?.response?.data?.detail;

  // FastAPI: `raise HTTPException(detail="...")`
  if (typeof detail === "string") return detail;

  // FastAPI/Pydantic v2 validation errors:
  //   detail: [{ type, loc, msg, input, ctx }, ...]
  if (Array.isArray(detail)) {
    const parts = detail
      .map(_formatValidationItem)
      .filter((s) => typeof s === "string" && s.length > 0);
    if (parts.length) return parts.join("; ");
    return GENERIC_ERR;
  }

  // Some backends nest a message
  if (detail && typeof detail === "object") {
    if (typeof detail.msg === "string") return detail.msg;
    if (typeof detail.message === "string") return detail.message;
    return GENERIC_ERR;
  }

  // Generic JS Error / axios NetworkError
  if (typeof e?.message === "string" && e.message) return e.message;

  return GENERIC_ERR;
};
